// History.js
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

/* ─── THEME TOKENS (same as Download page) ─── */
const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    accent: "#1a1a1a",
    accentText: "#ffffff",
    danger: "#d93025",
    inputBg: "#f7f7f8",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    accent: "#ececec",
    accentText: "#111111",
    danger: "#f28b82",
    inputBg: "#2a2a2a",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
  },
};

const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: 'Google Sans Text', 'Google Sans', sans-serif; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 999px; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }

  .fade-up { animation: fadeUp .25s ease forwards; }
  .fade-in { animation: fadeIn .2s ease; }

  .icon-btn {
    display: flex; align-items: center; justify-content: center;
    width: 38px; height: 38px; border-radius: 10px; border: 1px solid ${t.border};
    background: ${t.bg}; color: ${t.textSub};
    cursor: pointer; transition: .15s ease;
  }
  .icon-btn:hover { background: ${t.surface}; color: ${t.text}; }

  .primary-btn {
    border: none;
    background: ${t.accent};
    color: ${t.accentText};
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: .15s ease;
    box-shadow: ${t.shadow};
  }
  .primary-btn:hover:not(:disabled) { opacity: .85; transform: translateY(-1px); }

  .card {
    background: ${t.bg};
    border: 1px solid ${t.border};
    border-radius: 18px;
    box-shadow: ${t.shadow};
  }

  .material-icons-round { font-size: 20px; line-height: 1; user-select: none; }

  .markdown-body p {
    margin: 0 0 10px;
  }

  .markdown-body pre {
    overflow-x: auto;
    padding: 12px;
    border-radius: 12px;
    background: ${t.bgSub};
    border: 1px solid ${t.border};
  }

  .markdown-body code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
  }

  .markdown-body ul, .markdown-body ol {
    padding-left: 20px;
    margin: 8px 0;
  }

  .markdown-body blockquote {
    margin: 10px 0;
    padding: 10px 14px;
    border-left: 3px solid ${t.borderStrong};
    background: ${t.bgSub};
    border-radius: 10px;
  }
`;

function formatDate(ts) {
  if (!ts) return "Unknown date";
  try {
    if (typeof ts?.toDate === "function") return ts.toDate().toLocaleString();
    if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return "Unknown date";
  }
}

export default function History() {
  const navigate = useNavigate();

  const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const T = dark ? THEMES.dark : THEMES.light;

  const email = localStorage.getItem("email");
  const name = localStorage.getItem("name");
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const styleId = "omrali-history-theme";
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    styleTag.innerHTML = buildCSS(T);
  }, [T]);

  useEffect(() => {
    localStorage.setItem("darkMode", dark);
  }, [dark]);

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!email || !name || !userId) {
      navigate("/");
      return;
    }

    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, name, userId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError("");

    try {
      const q = query(
        collection(db, "conversations"),
        where("userId", "==", userId),
        orderBy("timestamp", "desc")
      );

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setHistory(data);
    } catch (err) {
      console.error("Error fetching history:", err);
      setError("Could not load your chat history.");
    } finally {
      setLoading(false);
    }
  };

  const Icon = ({ n, style }) => (
    <span className="material-icons-round" style={style}>
      {n}
    </span>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        color: T.text,
        fontFamily: "'Google Sans Text', 'Google Sans', sans-serif",
      }}
    >
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: T.bg,
          borderBottom: `1px solid ${T.border}`,
          padding: mobile ? "12px 14px" : "14px 22px",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              className="icon-btn"
              onClick={() => navigate(-1)}
              title="Go back"
            >
              <Icon n="arrow_back" />
            </button>

            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: mobile ? 16 : 17,
                  letterSpacing: "-0.2px",
                }}
              >
                Omrali <span style={{ color: T.textSub, fontWeight: 500 }}>AI</span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 2,
                }}
              >
                Your saved conversation history
              </div>
            </div>
          </div>

          <button
            className="icon-btn"
            onClick={() => setDark((d) => !d)}
            title="Toggle theme"
          >
            <Icon n={dark ? "light_mode" : "dark_mode"} />
          </button>
        </div>
      </header>

      {/* Main */}
      <main
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: mobile ? "18px 14px 28px" : "28px 22px 40px",
        }}
      >
        {/* Hero */}
        <section
          className="fade-in"
          style={{
            marginBottom: 22,
            padding: mobile ? "18px" : "22px",
            border: `1px solid ${T.border}`,
            borderRadius: 22,
            background: T.bgSub,
            boxShadow: T.shadow,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              borderRadius: 999,
              background: T.surface,
              border: `1px solid ${T.border}`,
              fontSize: 12,
              fontWeight: 600,
              color: T.textSub,
              marginBottom: 14,
            }}
          >
            <Icon n="history" style={{ fontSize: 16 }} />
            History Center
          </div>

          <h1
            style={{
              fontSize: mobile ? 26 : 36,
              lineHeight: 1.15,
              letterSpacing: "-0.8px",
              fontWeight: 800,
              margin: 0,
            }}
          >
            Your chat history
          </h1>

          <p
            style={{
              marginTop: 10,
              color: T.textSub,
              fontSize: 14.5,
              lineHeight: 1.7,
              maxWidth: 720,
            }}
          >
            View all saved Omrali AI conversations linked to your signed-in account.
            Markdown, code blocks, tables, and math are rendered properly for easy reading.
          </p>
        </section>

        {/* Stats */}
        <section
          className="fade-up"
          style={{
            display: "grid",
            gridTemplateColumns: mobile ? "1fr" : "repeat(3, 1fr)",
            gap: 14,
            marginBottom: 18,
          }}
        >
          {[
            {
              label: "Signed in as",
              value: name || "User",
              sub: email || "",
              icon: "person",
            },
            {
              label: "Saved conversations",
              value: String(history.length),
              sub: "Stored in account",
              icon: "forum",
            },
            {
              label: "Status",
              value: loading ? "Loading..." : "Ready",
              sub: loading ? "Fetching chats" : "History synced",
              icon: "sync",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="card"
              style={{
                padding: 18,
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon n={item.icon} style={{ fontSize: 20, color: T.textSub }} />
              </div>

              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: T.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                  }}
                >
                  {item.label}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 18,
                    fontWeight: 700,
                    color: T.text,
                    wordBreak: "break-word",
                  }}
                >
                  {item.value}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 13,
                    color: T.textSub,
                    wordBreak: "break-word",
                  }}
                >
                  {item.sub}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Error */}
        {error && (
          <div
            className="fade-up"
            style={{
              marginBottom: 16,
              padding: "14px 16px",
              borderRadius: 14,
              border: `1px solid ${T.border}`,
              background: dark ? "#2b1f1f" : "#fef2f2",
              color: T.danger,
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            {error}
          </div>
        )}

        {/* History List */}
        <section className="fade-up card" style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: mobile ? "16px 16px 14px" : "18px 20px 16px",
              borderBottom: `1px solid ${T.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                Saved chat preview
              </h3>
              <p
                style={{
                  marginTop: 6,
                  fontSize: 13.5,
                  color: T.textSub,
                }}
              >
                Showing your latest stored conversations from your account.
              </p>
            </div>

            <div
              style={{
                fontSize: 12,
                color: T.textMuted,
                fontWeight: 600,
                background: T.surface,
                border: `1px solid ${T.border}`,
                padding: "6px 10px",
                borderRadius: 999,
              }}
            >
              {loading ? "Loading..." : `${history.length} items`}
            </div>
          </div>

          <div
            style={{
              maxHeight: "calc(100vh - 280px)",
              overflowY: "auto",
              padding: mobile ? 14 : 18,
              background: T.bg,
            }}
          >
            {loading ? (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: T.textSub,
                  fontSize: 14,
                }}
              >
                Loading saved chats...
              </div>
            ) : history.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    margin: "0 auto 14px",
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon n="inbox" style={{ fontSize: 24, color: T.textSub }} />
                </div>

                <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                  No history found
                </h4>

                <p
                  style={{
                    marginTop: 8,
                    color: T.textSub,
                    fontSize: 14,
                    lineHeight: 1.7,
                    maxWidth: 520,
                    marginInline: "auto",
                  }}
                >
                  Start chatting while signed in, and your conversations will
                  appear here automatically.
                </p>

                <button
                  className="primary-btn"
                  style={{ marginTop: 14 }}
                  onClick={() => (window.location.href = "/")}
                >
                  Start Chatting
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {history.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    style={{
                      border: `1px solid ${T.border}`,
                      borderRadius: 16,
                      overflow: "hidden",
                      background: T.bgSub,
                    }}
                  >
                    {/* Top meta */}
                    <div
                      style={{
                        padding: "12px 14px",
                        borderBottom: `1px solid ${T.border}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          color: T.textSub,
                          fontWeight: 600,
                        }}
                      >
                        {formatDate(item.timestamp)}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: T.textMuted,
                          background: T.surface,
                          border: `1px solid ${T.border}`,
                          borderRadius: 999,
                          padding: "5px 9px",
                          fontWeight: 600,
                        }}
                      >
                        {item.model || "Unknown Model"}
                      </div>
                    </div>

                    {/* Content */}
                    <div style={{ padding: 14 }}>
                      {/* User bubble */}
                      <div
                        style={{
                          marginBottom: 12,
                          padding: 12,
                          borderRadius: 12,
                          background: T.accent,
                          color: T.accentText,
                          fontSize: 14,
                          lineHeight: 1.65,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            opacity: 0.8,
                            marginBottom: 6,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                          }}
                        >
                          You
                        </div>
                        {item.message || "—"}
                      </div>

                      {/* AI bubble */}
                      <div
                        style={{
                          padding: 12,
                          borderRadius: 12,
                          background: T.bg,
                          border: `1px solid ${T.border}`,
                          color: T.text,
                          fontSize: 14,
                          lineHeight: 1.7,
                          wordBreak: "break-word",
                          overflowX: "auto",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.textMuted,
                            marginBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: ".04em",
                          }}
                        >
                          Omrali AI
                        </div>

                        <div className="markdown-body">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            components={{
                              strong: ({ node, ...props }) => (
                                <strong style={{ color: T.text }} {...props} />
                              ),
                              p: ({ node, ...props }) => (
                                <p
                                  style={{
                                    margin: "0 0 10px",
                                    color: T.text,
                                  }}
                                  {...props}
                                />
                              ),
                              code({ inline, children, ...props }) {
                                return inline ? (
                                  <code
                                    style={{
                                      background: T.bgSub,
                                      border: `1px solid ${T.border}`,
                                      padding: "2px 6px",
                                      borderRadius: 6,
                                      color: T.text,
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                ) : (
                                  <code {...props}>{children}</code>
                                );
                              },
                              table: ({ node, ...props }) => (
                                <div style={{ overflowX: "auto", marginTop: 8 }}>
                                  <table
                                    style={{
                                      borderCollapse: "collapse",
                                      width: "100%",
                                      minWidth: 500,
                                    }}
                                    {...props}
                                  />
                                </div>
                              ),
                              th: ({ node, ...props }) => (
                                <th
                                  style={{
                                    border: `1px solid ${T.border}`,
                                    padding: "8px 10px",
                                    backgroundColor: T.bgSub,
                                    color: T.text,
                                    textAlign: "left",
                                  }}
                                  {...props}
                                />
                              ),
                              td: ({ node, ...props }) => (
                                <td
                                  style={{
                                    border: `1px solid ${T.border}`,
                                    padding: "8px 10px",
                                    color: T.text,
                                  }}
                                  {...props}
                                />
                              ),
                              blockquote: ({ node, ...props }) => (
                                <blockquote
                                  style={{
                                    margin: "10px 0",
                                    padding: "10px 14px",
                                    borderLeft: `3px solid ${T.borderStrong}`,
                                    background: T.bgSub,
                                    borderRadius: 10,
                                    color: T.textSub,
                                  }}
                                  {...props}
                                />
                              ),
                            }}
                          >
                            {item.response || "—"}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}